/*======================================================================================================================
  * Bluebell: src/app/state/projects/projects.effects.ts
  * Last Edited: 20/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { updateUserAccountSettings } from '@state/account/account.actions';
import { UserAccountSettings } from '@state/account/account.interfaces';
import { selectCurrentUserAccountSettings } from '@state/account/account.selectors';
import { ApiService } from '@state/api.service';
import { ProgrammeActions } from '@state/programmes/programme.actions';
import { Programme } from '@state/programmes/programme.interfaces';
import { ProjectActions } from '@state/projects/project.actions';
import {
  FullProject,
  ProjectCreatePartial,
  Project,
  ProjectUpdatePartial,
} from '@state/projects/project.interfaces';
import { selectCurrentProject, selectShowInactiveProjects } from '@state/projects/project.selectors';
import { BaselineRun } from '@state/runs/run.interfaces';
import { concatMap, mergeMap, of, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';


@Injectable()
export class ProjectEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);
  private api = inject(ApiService);

  loadInactive = this.store.selectSignal(selectShowInactiveProjects);

  /*
      CRUD
  */

  create$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.create),
    mergeMap((projectPartial: ProjectCreatePartial) => this.api.createProject(projectPartial).pipe(
      this.handler.effectResponse({
        success: (project: Project) => ProjectActions.createSuccess(project),
        retry: ProjectActions.create(projectPartial),
        failure: ProjectActions.createFailure(),
        failureMessage: 'Could not create project',
      }),
    )),
  ));

  subscribeToNewProjects$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.createSuccess),
    concatLatestFrom(() => this.store.select(selectCurrentUserAccountSettings)),
    filter((payload): payload is [ReturnType<typeof ProjectActions.createSuccess>, UserAccountSettings] => !!payload[1]),
    concatMap(([project, userAccountSettings]: [Project, UserAccountSettings]) => {

      const subscribed_projects = [...userAccountSettings.subscribed_projects];
      if (
        userAccountSettings.subscribed_programmes.includes(project.programme)
        && !userAccountSettings.subscribed_projects.includes(project.uuid)
      ) {
        subscribed_projects.push(project.uuid);
      }

      return of(updateUserAccountSettings({ settings: { subscribed_projects } }));
    }),
  ));

  load$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.load),
    concatLatestFrom(() => [
      this.store.select(selectCurrentUserAccountSettings),
      this.store.select(selectCurrentProject),
    ]),
    switchMap(([queryParams, userAccountSettings, currentProject]) =>
      this.api.listProjects(queryParams, !this.loadInactive() || null).pipe(
        this.handler.effectResponse({
          success: (projects) => {
            // If there is a project selected or there is a project specified in the user account settings,
            // retrieve the full data of the selected project.
            const selectedProject = projects.find((project: Project) => project.uuid
                                                                        === userAccountSettings?.current_project);
            if (selectedProject && selectedProject.uuid !== currentProject?.uuid) {
              this.store.dispatch(ProjectActions.select(selectedProject));
            }
            return ProjectActions.loadSuccess(projects);
          },
          retry: ProjectActions.load(queryParams),
          failure: ProjectActions.loadFailure(),
          failureMessage: 'Could not load project',
        }),
      )),
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.update),
    concatMap((projectPartial: ProjectUpdatePartial) => this.api.updateProject(projectPartial).pipe(
      this.handler.effectResponse({
        success: (project: Project) => ProjectActions.updateSuccess(project),
        retry: ProjectActions.update(projectPartial),
        failure: ProjectActions.updateFailure(),
        failureMessage: 'Could not update project',
      }),
    )),
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.delete),
    mergeMap((project: Project) => this.api.deleteProject(project.uuid).pipe(
      this.handler.effectResponse({
        success: () => ProjectActions.deleteSuccess(project),
        retry: ProjectActions.delete(project),
        failure: ProjectActions.deleteFailure(),
        failureMessage: 'Could not delete project',
      }),
    )),
  ));

  /*
      State
  */
  toggleLoadInactive$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.toggleLoadInactive),
    map((queryParams) => ProjectActions.load(queryParams)),
  ));

  archiveProject$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.archive),
    mergeMap((project: Project) => this.api.archiveProject(project.uuid).pipe(
      this.handler.effectResponse({
        success: () => {
          const updatedProject: Project = {
            ...project,
            cases_archived: {
              any: true,
              all: true,
              can_archive: false,
            },
          };
          return ProjectActions.archiveSuccess(updatedProject);
        },
        retry: ProjectActions.archive(project),
        failure: ProjectActions.archiveFailure(),
        failureMessage: 'Could not archive project',
        successMessage: `Project "${project.name}" is being archived.`,
      }),
    )),
  ));

  onProgrammeSelect$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.select),
    concatLatestFrom(() => this.store.select(selectCurrentProject)),
    filter(([programme, project]: [Programme, FullProject|null]) => programme.uuid !== project?.programme),
    switchMap(([programme]: [Programme, FullProject|null]) => {
      // Deselect the current project and reload the projects for the newly selected programme.
      this.store.dispatch(ProjectActions.deselect());
      return this.api.listProjects({
        project_table: true,
        selected_programme: programme.uuid,
      }, !this.loadInactive()).pipe(
        map((projects: Project[]) => ProjectActions.loadSuccess(projects)),
      );
    }),
  ));

  /**
   * An effect to select a project based on the reduced project data in the projects dropdowns and tables.
   */
  onProjectSelect$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.select),
    switchMap((tableProject) => of(ProjectActions.selectByUUID({ uuid: tableProject.uuid }))),
  ));

  // localStorageSetProject$ = createEffect(() => this.actions$.pipe(
  //   ofType(ProjectActions.localStorageSetCurrentProject),
  //   map(({ selectedId }) => ProjectActions.selectByUUID({ uuid: selectedId }))
  // ));

  /**
   * An effect to retrieve the full project data of a single project based on a Project UUID.
   */

  selectProjectByUUID$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.selectByUUID),
    switchMap((project: {uuid: string}) => this.api.retrieveProject(project.uuid).pipe(
      this.handler.effectResponse({
        success: (fullProject: FullProject) => ProjectActions.selectSuccess(fullProject),
        retry: ProjectActions.selectByUUID({ uuid: project.uuid }),
        failure: ProjectActions.selectFailure(),
      }),
    )),
  ));

  OnProgrammesDeselect$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.deselect),
    map(() => {
      this.store.dispatch(ProjectActions.clearAll());
      return ProjectActions.deselect();
    }),
  ));

  loadProjectBaselineOnSelect$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.selectSuccess),
    map(({ default_baseline_run }: FullProject) => {
      if (default_baseline_run) {
        return ProjectActions.loadProjectBaseline(default_baseline_run);

      } else {
        return ProjectActions.clearProjectBaseline();
      }
    }),
  ));

  loadProjectBaseline$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.loadProjectBaseline),
    switchMap(({ uuid }) => this.api.runGetFullName<BaselineRun>(uuid, 'cases_archived,project__programme__uuid,project__uuid,uuid').pipe(
      this.handler.effectResponse({
        retry: ProjectActions.loadProjectBaseline(uuid),
        success: (baselineRun: BaselineRun) => ProjectActions.loadProjectBaselineSuccess(baselineRun),
        failure: ({ error }: HttpErrorResponse) => ProjectActions.loadProjectBaselineFailure(error),
        failureMessage: 'Could not load project baseline',
      }),
    )),
  ));
}
